import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { Goal } from '@/types/goal';
import { format } from 'date-fns';

export interface JourneyPhase {
  id: string;
  name: string;
  shortName: string;
  description: string;
  icon: string;
  tabValue: string;
  order: number;
  isComplete: boolean;
  progress: number;
  nextStepPrompt: string;
  requiredForNext: boolean;
}

export interface JourneyProgress {
  phases: JourneyPhase[];
  currentPhase: JourneyPhase | null;
  nextPhase: JourneyPhase | null;
  overallProgress: number;
  isLoading: boolean;
  refetch: () => Promise<void>;
}

export const useJourneyProgress = (goals: Goal[] = []): JourneyProgress => {
  const { user, loading: authLoading } = useAuth();
  const { effectiveUserId } = useImpersonation();
  const [isLoading, setIsLoading] = useState(true);
  const [coreValuesCount, setCoreValuesCount] = useState(0);
  const [hasDevPlan, setHasDevPlan] = useState(false);
  const [mentorshipConnectionsCount, setMentorshipConnectionsCount] = useState(0);
  const [hasMentorProfile, setHasMentorProfile] = useState(false);

  const fetchProgressData = async () => {
    // IMPORTANT: `useAuth()` can still be loading even inside a ProtectedRoute,
    // because this hook may be used in a different component tree.
    // Avoid marking journey progress as "not loading" until auth is resolved.
    if (authLoading) return;

    // Use effectiveUserId for impersonation, fallback to user.id
    const userId = effectiveUserId || user?.id;

    if (!userId) {
      // Auth resolved and no user => treat as no journey progress.
      setCoreValuesCount(0);
      setHasDevPlan(false);
      setMentorshipConnectionsCount(0);
      setHasMentorProfile(false);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      // Single RPC replaces 4 parallel queries for fewer round-trips
      const { data, error } = await supabase.rpc('get_journey_progress' as string, {
        p_user_id: userId,
      });

      if (error) {
        console.error('Error fetching journey progress:', error);
      } else {
        const row = Array.isArray(data) ? data[0] : data;
        setCoreValuesCount(Number(row?.core_values_count ?? 0));
        setHasDevPlan(row?.has_dev_plan ?? false);
        setMentorshipConnectionsCount(Number(row?.mentorship_connections_count ?? 0));
        setHasMentorProfile(row?.has_mentor_profile ?? false);
      }
    } catch (error) {
      console.error('Error fetching journey progress:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    fetchProgressData();
  }, [user, authLoading, effectiveUserId]);

  const phases = useMemo((): JourneyPhase[] => {
    // Calculate habit completion
    const totalHabits = goals.reduce((acc, g) => acc + g.habits.length, 0);
    const today = format(new Date(), 'yyyy-MM-dd');
    const completedHabitsToday = goals.reduce((acc, g) => 
      acc + g.habits.filter(h => h.completedDates.includes(today)).length, 0
    );
    const habitsProgress = totalHabits > 0 ? Math.round((completedHabitsToday / totalHabits) * 100) : 0;

    return [
      {
        id: 'values',
        name: 'Core Values',
        shortName: 'Values',
        description: 'Discover what matters most to you',
        icon: 'Heart',
        tabValue: 'values',
        order: 1,
        isComplete: coreValuesCount >= 3,
        progress: Math.min(100, Math.round((coreValuesCount / 3) * 100)),
        nextStepPrompt: coreValuesCount === 0
          ? "Start by discovering your core values — they unlock goal setting."
          : coreValuesCount < 3
            ? `Complete ${3 - coreValuesCount} more value${3 - coreValuesCount === 1 ? '' : 's'} to unlock goal setting.`
            : "Great foundation! Now set goals aligned with your values.",
        requiredForNext: true,
      },
      {
        id: 'goals',
        name: 'Goal Setting',
        shortName: 'Goals',
        description: 'Set meaningful career goals',
        icon: 'Target',
        tabValue: 'goals',
        order: 2,
        isComplete: goals.length >= 1,
        progress: goals.length > 0 ? Math.min(100, Math.round((goals.length / 3) * 100)) : 0,
        nextStepPrompt: goals.length === 0 
          ? "Set your first goal based on your core values."
          : goals.length < 3 
            ? `You have ${goals.length} goal${goals.length === 1 ? '' : 's'}. Consider adding goals across Education, Exposure, and Experience.`
            : "Excellent goals! Get AI-powered insights on your skill gaps.",
        requiredForNext: true,
      },
      {
        id: 'plan',
        name: 'Development Plan',
        shortName: 'Plan',
        description: 'Create your growth roadmap',
        icon: 'Map',
        tabValue: 'plan',
        order: 3,
        isComplete: hasDevPlan,
        progress: hasDevPlan ? 100 : 0,
        nextStepPrompt: !hasDevPlan
          ? "Generate your development plan — a 6-month roadmap with milestones."
          : "Plan created! Add daily habits to start executing on milestones.",
        requiredForNext: false,
      },
      {
        id: 'habits',
        name: 'Habits',
        shortName: 'Habits',
        description: 'Build consistent daily practices',
        icon: 'CheckCircle2',
        tabValue: 'habits',
        order: 4,
        isComplete: habitsProgress >= 80,
        progress: habitsProgress,
        nextStepPrompt: totalHabits === 0
          ? "Add habits to your goals to build daily momentum."
          : habitsProgress < 80
            ? `${completedHabitsToday}/${totalHabits} habits done today — ${totalHabits - completedHabitsToday} to go!`
            : "Amazing consistency! Consider connecting with a mentor for guidance.",
        requiredForNext: false,
      },
      {
        id: 'mentorship',
        name: 'Mentorship',
        shortName: 'Mentor',
        description: 'Connect with guides and peers',
        icon: 'Users',
        tabValue: 'mentorship',
        order: 5,
        isComplete: mentorshipConnectionsCount > 0 || hasMentorProfile,
        progress: hasMentorProfile ? 50 : 0 + (mentorshipConnectionsCount > 0 ? 50 : 0),
        nextStepPrompt: !hasMentorProfile && mentorshipConnectionsCount === 0
          ? "Set up your mentor profile or find a mentor to accelerate your growth."
          : hasMentorProfile && mentorshipConnectionsCount === 0
            ? "Great profile! Now find mentors or mentees to connect with."
            : "You're connected! Keep nurturing your mentorship relationships.",
        requiredForNext: false,
      },
    ];
  }, [coreValuesCount, goals, hasDevPlan, mentorshipConnectionsCount, hasMentorProfile]);

  const currentPhase = useMemo(() => {
    return phases.find(p => !p.isComplete) || phases[phases.length - 1];
  }, [phases]);

  const nextPhase = useMemo(() => {
    const currentIndex = phases.findIndex(p => p.id === currentPhase?.id);
    return currentIndex < phases.length - 1 ? phases[currentIndex + 1] : null;
  }, [phases, currentPhase]);

  const overallProgress = useMemo(() => {
    const totalProgress = phases.reduce((acc, p) => acc + p.progress, 0);
    return Math.round(totalProgress / phases.length);
  }, [phases]);

  return {
    phases,
    currentPhase,
    nextPhase,
    overallProgress,
    isLoading,
    refetch: fetchProgressData,
  };
};
